import { CapacitorHttp, HttpResponseType } from '@capacitor/core';
import { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import * as firebaseAuth from 'firebase/auth';
import * as oidc from 'oidc-client-ts';
import { KeycloakToken } from 'src/libs/keycloak';

// auto-injected tokens
let firebaseCurrentUser: firebaseAuth.User | null = null;
let keycloakToken: KeycloakToken | null = null;
let oidcUser: oidc.User | null = null;

export const defaultHeaders = {
  Accept: '*/*',
  'Content-Type': 'application/json'
};

export interface CapacitorBaseQueryOptions {
  baseUrl: string;
  enableAuth?: boolean;
  abacPreflight?: boolean;
}

export interface CapacitorBaseQueryArgs {
  url: string;
  method?: string;
  body?: any;
  params?: { [key: string]: any };
  headers?: { [key: string]: string };
}

const injectFirebaseAuth = async (args: CapacitorBaseQueryArgs) => {
  // use the current (valid) token or refresh the token now
  // (this avoids manual token management)
  const token = await firebaseCurrentUser?.getIdToken();

  if (token)
    args.headers = {
      ...args.headers,
      Authorization: `Bearer ${token}`
    };
};

const injectKeycloakAuth = (args: CapacitorBaseQueryArgs) => {
  if (keycloakToken?.access_token)
    args.headers = {
      ...args.headers,
      Authorization: `Bearer ${keycloakToken.access_token}`
    };
};

const injectOidcAuth = (args: CapacitorBaseQueryArgs) => {
  if (oidcUser?.access_token)
    args.headers = {
      ...args.headers,
      Authorization: `Bearer ${oidcUser.id_token}`
    };
};

const injectAbacPreflight = (args: CapacitorBaseQueryArgs) => {
  args.headers = {
    ...args.headers,
    'X-ABAC-Preflight': 'true'
  };
};

const adjustParams = (params: { [key: string]: any }) =>
  Object.fromEntries(
    Object.entries(params)
      // filter undefined params
      .filter(([key, value]) => value !== undefined)
      // stringify object params
      .map(([key, value]) => [
        key,
        typeof value === 'object' && !Array.isArray(value)
          ? JSON.stringify(value)
          : value
      ])
  );

/**
 * RTK base query which uses capacitor HTTP to abstract fetch.
 *
 * This allows to use native request APIs on non-web platforms,
 * and therefore enables cross-origin requests.
 */
export const capacitorBaseQuery =
  ({
    baseUrl,
    enableAuth,
    abacPreflight
  }: CapacitorBaseQueryOptions): BaseQueryFn<
    CapacitorBaseQueryArgs,
    unknown,
    unknown,
    { responseType?: HttpResponseType } | undefined
  > =>
  async (args, api, extraOptions) => {
    if (enableAuth) {
      const authProvider = globalThis.__TENANT__?.authProvider;
      if (authProvider === 'firebase') await injectFirebaseAuth(args);
      if (authProvider === 'keycloak') injectKeycloakAuth(args);
      if (authProvider === 'oidc') injectOidcAuth(args);
    }

    if (abacPreflight) injectAbacPreflight(args);

    const { url, method, body, params, headers } = args;
    const result = await CapacitorHttp.request({
      url: baseUrl + url,
      method,
      data: body,
      params: params && adjustParams(params),
      headers: { ...defaultHeaders, ...headers },
      responseType: extraOptions?.responseType
    });

    if (result.status >= 200 && result.status < 400)
      return { ...result, meta: result };

    return { error: result };
  };

export const updateFirebaseCurrentUser = (user: firebaseAuth.User | null) => {
  firebaseCurrentUser = user;
  // authenticated requests available
  console.info('[capacitor-adapter] Firebase user changed:', !!user);
};

export const updateKeycloakToken = (token: KeycloakToken | null) => {
  keycloakToken = token;
  // authenticated requests available
  console.info('[capacitor-adapter] Keycloak token changed:', !!token);
};

export const updateOidcUser = (user: oidc.User | null) => {
  oidcUser = user;
  // authenticated requests available
  console.info('[capacitor-adapter] OIDC user changed:', !!user);
};
